// The time window every card on the session canvas reads: the whole clock
// across the top (long pauses closed to a break), the window a stretch of it,
// and a cursor inside the window past which the canvas dims what had not
// happened yet. Drag inside the window to move it, its edges to resize it,
// outside it to draw a new one; click inside to place the cursor, double-click
// to lift it; scroll to zoom the window around the pointer.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CLOCK_U, MIN_WINDOW_U, clampWindow, spanText } from "../lib/sessionWorkbench.js";
import { CANVAS_SANS as SANS, alphaMix, clipText, overMix, readTheme } from "./LanesCanvasPlane.jsx";
import { useThemeVersion } from "../lib/theme.js";

export const SCRUBBER_H = 58;
const LABEL_W = 176;
const PAD_R = 14;
const EDGE_PX = 6;
const TRACK_TOP = 16;
const HIST_Y = 18;
const HIST_H = 16;
const SPAN_Y = 38;
const SPAN_ROWS = 3;
const DOT = " \u00b7 ";

// One formatter each, made once: a toLocale call builds its own every time.
const DAY_FMT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const CLOCK_FMT = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
const CLOCK_SEC_FMT = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
const dayText = (ms) => DAY_FMT.format(ms);
const clockText = (ms, sec = false) => (sec ? CLOCK_SEC_FMT : CLOCK_FMT).format(ms);
const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

export function rangeText(t0, t1) {
  if (!Number.isFinite(t0) || !Number.isFinite(t1)) return "";
  const sec = t1 - t0 < 10 * 60_000;
  return sameDay(t0, t1)
    ? `${dayText(t0)}, ${clockText(t0, sec)} - ${clockText(t1, sec)}`
    : `${dayText(t0)} ${clockText(t0)} - ${dayText(t1)} ${clockText(t1)}`;
}

export default function SessionTimeScrubber({ clock, win, cursor, onWindow, onCursor, activity = [], spans = [] }) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  const themeVersion = useThemeVersion();
  const theme = useMemo(() => readTheme(), [themeVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const drag = useRef(null);
  const [hoverMode, setHoverMode] = useState(null);

  useLayoutEffect(() => {
    const el = ref.current?.parentElement;
    if (!el) return undefined;
    const measure = () => setW(Math.floor(el.getBoundingClientRect().width));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // A hidden pane never delivers the observer's callback; the window's
    // resize still arrives.
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, []);

  const x0 = LABEL_W;
  const x1 = Math.max(x0 + 40, w - PAD_R);
  const tw = x1 - x0;
  const X = useCallback((u) => x0 + (u / CLOCK_U) * tw, [x0, tw]);
  const U = useCallback((x) => Math.max(0, Math.min(CLOCK_U, ((x - x0) / tw) * CLOCK_U)), [x0, tw]);

  // Activity per 2px column, the open trace's apart from everyone else's.
  const hist = useMemo(() => {
    const bins = Math.max(1, Math.floor(tw / 2));
    const all = new Float64Array(bins);
    const mine = new Float64Array(bins);
    for (const a of activity) {
      for (const u of a.u || []) {
        if (u == null) continue;
        const b = Math.min(bins - 1, Math.max(0, Math.floor((u / CLOCK_U) * bins)));
        all[b] += 1;
        if (a.focal) mine[b] += 1;
      }
    }
    let max = 0;
    for (const v of all) if (v > max) max = v;
    return { bins, all, mine, max };
  }, [activity, tw]);

  // Every session's span, packed into a few thin rows.
  const packed = useMemo(() => {
    const rows = new Array(SPAN_ROWS).fill(-Infinity);
    return [...spans].sort((a, b) => (b.focal - a.focal) || a.u0 - b.u0).map((s) => {
      let row = rows.findIndex((end) => end < s.u0 - 2);
      if (row < 0) row = SPAN_ROWS - 1;
      rows[row] = Math.max(rows[row], s.u1);
      return { ...s, row };
    });
  }, [spans]);

  const draw = useCallback(() => {
    const cv = ref.current;
    if (!cv || !w) return;
    const dpr = window.devicePixelRatio || 1;
    const H = SCRUBBER_H;
    if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(H * dpr)) {
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(H * dpr);
      cv.style.width = `${w}px`;
      cv.style.height = `${H}px`;
    }
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = theme.bg;
    ctx.fillRect(0, 0, w, H);
    ctx.fillStyle = theme.lineStrong;
    ctx.fillRect(0, H - 1, w, 1);
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
    // The label column: what the window is, then where it sits.
    ctx.font = `11px ${SANS}`;
    ctx.letterSpacing = "0.06em";
    ctx.fillStyle = theme.eyebrow;
    ctx.fillText("TIME WINDOW", 12, 17);
    ctx.letterSpacing = "0px";
    if (!clock) {
      ctx.font = `11px ${SANS}`;
      ctx.fillStyle = theme.muted;
      ctx.fillText("No times to place these sessions on a clock.", x0, 32);
      return;
    }
    const t0 = clock.inv(win.u0);
    const t1 = clock.inv(win.u1);
    const pauses = clock.breaks.filter((b) => b.u0 >= win.u0 && b.u1 <= win.u1).length;
    ctx.font = `500 12px ${SANS}`;
    ctx.fillStyle = theme.ink;
    ctx.fillText(clipText(ctx, `${spanText(t1 - t0)}${pauses ? `${DOT}${pauses} pause${pauses > 1 ? "s" : ""} closed` : ""}`, LABEL_W - 20), 12, 34);
    ctx.font = `10.5px ${SANS}`;
    ctx.fillStyle = theme.muted;
    ctx.fillText(clipText(ctx, rangeText(t0, t1), LABEL_W - 20), 12, 49);

    // Where each burst of the clock begins.
    ctx.font = `9.5px ${SANS}`;
    let lastLabelEnd = -Infinity;
    for (const s of clock.iv) {
      if (s.gap || s.first === false) continue;
      const x = X(s.u0);
      const text = `${dayText(s.t0)} ${clockText(s.t0)}`;
      const tw2 = ctx.measureText(text).width;
      if (x < lastLabelEnd + 8 || x + tw2 > x1) continue;
      ctx.fillStyle = theme.mutedSoft;
      ctx.fillText(text, x, 11);
      lastLabelEnd = x + tw2;
    }
    // Activity, everyone's under the open trace's.
    const bw = tw / hist.bins;
    if (hist.max > 0) {
      for (let i = 0; i < hist.bins; i++) {
        if (!hist.all[i]) continue;
        const h = Math.max(1, Math.sqrt(hist.all[i] / hist.max) * HIST_H);
        ctx.fillStyle = alphaMix(theme.muted, 60);
        ctx.fillRect(x0 + i * bw, HIST_Y + HIST_H - h, Math.max(1, bw - 0.5), h);
        if (hist.mine[i]) {
          const hm = Math.max(1, Math.sqrt(hist.mine[i] / hist.max) * HIST_H);
          ctx.fillStyle = theme.ink;
          ctx.fillRect(x0 + i * bw, HIST_Y + HIST_H - hm, Math.max(1, bw - 0.5), hm);
        }
      }
    }
    // Each session's span, in its hue; the open trace's in ink.
    for (const s of packed) {
      const a = X(s.u0);
      const b = Math.max(a + 2, X(s.u1));
      ctx.fillStyle = s.focal ? theme.ink : s.hue || theme.muted;
      ctx.fillRect(a, SPAN_Y + s.row * 5, b - a, 3);
    }
    // The pauses the clock closed.
    for (const b of clock.breaks) {
      const a = X(b.u0);
      const e = X(b.u1);
      ctx.fillStyle = theme.bg;
      ctx.fillRect(a, TRACK_TOP, e - a, H - TRACK_TOP - 4);
      ctx.strokeStyle = theme.muted;
      ctx.lineWidth = 1;
      const cx = (a + e) / 2;
      ctx.beginPath();
      ctx.moveTo(cx - 3.5, HIST_Y + HIST_H);
      ctx.lineTo(cx - 0.5, HIST_Y + 4);
      ctx.moveTo(cx + 0.5, HIST_Y + HIST_H);
      ctx.lineTo(cx + 3.5, HIST_Y + 4);
      ctx.stroke();
      if (e - a >= 16) {
        ctx.font = `9px ${SANS}`;
        ctx.fillStyle = theme.mutedSoft;
        ctx.textAlign = "center";
        ctx.fillText(b.label, cx, SPAN_Y + 12);
        ctx.textAlign = "left";
      }
    }
    // Outside the window, quieter; the window itself framed, with handles.
    const wa = X(win.u0);
    const wb = X(win.u1);
    ctx.fillStyle = alphaMix(theme.bg, 62);
    ctx.fillRect(x0 - 2, 2, Math.max(0, wa - x0 + 2), H - 5);
    ctx.fillRect(wb, 2, Math.max(0, x1 - wb + 2), H - 5);
    ctx.strokeStyle = theme.ink;
    ctx.lineWidth = 1;
    ctx.strokeRect(Math.round(wa) + 0.5, 3.5, Math.max(1, Math.round(wb - wa) - 1), H - 8);
    for (const [x, mode] of [[wa, "left"], [wb, "right"]]) {
      ctx.fillStyle = hoverMode === mode ? theme.ink : overMix(theme.controlBg, 100, theme.bg);
      ctx.fillRect(x - 2, H / 2 - 8, 4, 16);
      ctx.strokeStyle = theme.ink;
      ctx.strokeRect(x - 2 + 0.5, H / 2 - 8 + 0.5, 3, 15);
    }
    // The cursor: everything after it on the canvas is still to come.
    if (cursor != null) {
      const cx = X(cursor);
      ctx.fillStyle = alphaMix(theme.bg, 45);
      ctx.fillRect(cx, 4, Math.max(0, wb - cx - 1), H - 9);
      ctx.fillStyle = theme.green;
      ctx.fillRect(Math.round(cx), 3, 1.5, H - 7);
      ctx.beginPath();
      ctx.moveTo(cx - 5, 1);
      ctx.lineTo(cx + 6, 1);
      ctx.lineTo(cx + 0.5, 8);
      ctx.closePath();
      ctx.fill();
      const t = clock.inv(cursor);
      const text = clockText(t, t1 - t0 < 6 * 3600_000);
      ctx.font = `500 10px ${SANS}`;
      const tw3 = ctx.measureText(text).width;
      const lx = cx + 7 + tw3 > x1 ? cx - 7 - tw3 : cx + 7;
      ctx.fillStyle = overMix(theme.controlBg, 100, theme.bg);
      ctx.fillRect(lx - 3, 3, tw3 + 6, 13);
      ctx.fillStyle = theme.ink;
      ctx.fillText(text, lx, 13);
    }
  }, [w, theme, clock, win, cursor, X, x0, x1, tw, hist, packed, hoverMode]);

  useLayoutEffect(() => { draw(); }, [draw]);

  const modeAt = useCallback((x, y) => {
    if (x < x0 - 4) return null;
    if (cursor != null && Math.abs(x - X(cursor)) <= 5 && y < 18) return "cursor";
    if (Math.abs(x - X(win.u0)) <= EDGE_PX) return "left";
    if (Math.abs(x - X(win.u1)) <= EDGE_PX) return "right";
    return x > X(win.u0) && x < X(win.u1) ? "inside" : "outside";
  }, [x0, X, win, cursor]);

  const onPointerDown = (e) => {
    if (!clock) return;
    const r = ref.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const mode = modeAt(x, y);
    if (!mode) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { mode, x, u: U(x), win: { ...win }, moved: false };
  };
  const onPointerMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const d = drag.current;
    if (!d) {
      const m = clock ? modeAt(x, y) : null;
      if (m !== hoverMode) setHoverMode(m);
      return;
    }
    if (!d.moved && Math.abs(x - d.x) < 3) return;
    d.moved = true;
    const u = U(x);
    const width = d.win.u1 - d.win.u0;
    if (d.mode === "cursor") onCursor(Math.max(win.u0, Math.min(win.u1, u)));
    else if (d.mode === "left") onWindow(clampWindow({ u0: Math.min(u, d.win.u1 - MIN_WINDOW_U), u1: d.win.u1 }));
    else if (d.mode === "right") onWindow(clampWindow({ u0: d.win.u0, u1: Math.max(u, d.win.u0 + MIN_WINDOW_U) }));
    else if (d.mode === "inside") {
      const shift = u - d.u;
      const u0 = Math.max(0, Math.min(CLOCK_U - width, d.win.u0 + shift));
      onWindow({ u0, u1: u0 + width });
    } else onWindow(clampWindow({ u0: Math.min(d.u, u), u1: Math.max(d.u, u) }));
  };
  const onPointerUp = (e) => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.moved) return;
    const r = ref.current.getBoundingClientRect();
    const u = U(e.clientX - r.left);
    if (d.mode === "inside") {
      // A cursor at the window's end is no cursor: nothing is still to come.
      onCursor(u >= win.u1 - (win.u1 - win.u0) * 0.01 ? null : u);
    } else if (d.mode === "outside") {
      const width = win.u1 - win.u0;
      const u0 = Math.max(0, Math.min(CLOCK_U - width, u - width / 2));
      onWindow({ u0, u1: u0 + width });
    }
  };

  // Scroll zooms the window around the pointer; a sideways scroll slides it.
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return undefined;
    const onWheel = (e) => {
      if (!clock) return;
      e.preventDefault();
      const r = cv.getBoundingClientRect();
      const at = U(e.clientX - r.left);
      const width = win.u1 - win.u0;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        const shift = (e.deltaX / tw) * CLOCK_U;
        const u0 = Math.max(0, Math.min(CLOCK_U - width, win.u0 + shift));
        onWindow({ u0, u1: u0 + width });
        return;
      }
      const f = Math.exp(e.deltaY * 0.0025);
      const next = Math.max(MIN_WINDOW_U, Math.min(CLOCK_U, width * f));
      const k = width > 0 ? (at - win.u0) / width : 0.5;
      const u0 = Math.max(0, Math.min(CLOCK_U - next, at - k * next));
      onWindow({ u0, u1: u0 + next });
    };
    cv.addEventListener("wheel", onWheel, { passive: false });
    return () => cv.removeEventListener("wheel", onWheel);
  }, [clock, win, U, tw, onWindow]);

  const cursorStyle = hoverMode === "left" || hoverMode === "right" || hoverMode === "cursor" ? "ew-resize"
    : hoverMode === "inside" ? "grab" : hoverMode === "outside" ? "crosshair" : "default";
  return (
    <div className="relative shrink-0" style={{ height: SCRUBBER_H }} data-session-scrubber="">
      <canvas
        ref={ref}
        className="absolute inset-0 block"
        style={{ cursor: cursorStyle, touchAction: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={() => { if (!drag.current) setHoverMode(null); }}
        onDoubleClick={() => onCursor(null)}
        aria-label="Time window"
      />
    </div>
  );
}
